/**
 * Phase 8.1 — Attribution capture.
 *
 * Captures Meta/Google/UTM/etc. attribution parameters from the current page
 * URL when a visitor arrives on Q Drives, and persists them in sessionStorage
 * until the seller submits the form.
 *
 * Strictly capture + store + read. No analytics calls, no Meta API, no PII.
 */
const STORAGE_KEY = "qdrives_attribution";

const WHITELIST = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "fbclid", "gclid",
  "campaign_id", "adset_id", "ad_id",
  "meta_campaign_id", "meta_adset_id", "meta_ad_id",
  "placement", "platform",
  "lead_source_detail",
  "campaign_name",
];

/** Read the current URL query params and persist whitelisted ones + referrer. */
export function captureAttributionFromURL() {
  try {
    if (typeof window === "undefined") return null;
    const url    = new URL(window.location.href);
    const params = {};
    let captured = 0;
    WHITELIST.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) { params[k] = v.slice(0, 512); captured += 1; }
    });
    // Even when no whitelisted params are present, still record referrer +
    // landing_page on the very first visit so we know it was a direct/organic
    // visit when the form is submitted later.
    const existing = readAttribution();
    if (!existing && captured === 0 && !document.referrer) {
      return null; // truly empty — don't pollute storage.
    }

    const payload = {
      params,
      referrer:     existing?.referrer || document.referrer || "",
      landing_page: existing?.landing_page || (url.origin + url.pathname),
      captured_at:  existing?.captured_at || new Date().toISOString(),
      latest_captured_at: new Date().toISOString(),
    };
    // Merge instead of overwrite — later visits can ADD params (e.g. user
    // bounces between pages) but never strip earlier ones.
    if (existing && existing.params) {
      payload.params = { ...existing.params, ...params };
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return null;
  }
}

/** Read the stored attribution payload (or null). */
export function readAttribution() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Clear stored attribution. Called after a successful form submission. */
export function clearAttribution() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
}

/** Return the payload to send with `/api/vehicles/submit`. */
export function buildAttributionSubmitPayload() {
  const a = readAttribution();
  if (!a) return null;
  return {
    params:       a.params || {},
    referrer:     a.referrer || "",
    landing_page: a.landing_page || "",
  };
}
